import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  setDefaultAPIError,
  setUserActiveSession,
  updateWidgetConfig,
  setLoading as setGetHierarchyLoading,
} from "../../store/actions";
import { Loader } from "@vds/loaders";
import { TextLink, TextLinkCaret } from "@vds/buttons";
import { Input } from "@vds/inputs";
import { DropdownSelect } from "@vds/selects";
import { accountDetailsApi, invalidateTokenApi, tokenApi } from "../../api/SessionServiceManager";
import { setCookie, signInConfig } from "../../global/utility/Utils";
import FiveGVideoInsightsPage from "./MFALanding";
import { STORAGE } from "../../global/constants/storageConstants";
import { LOGIN_ERROR_MESSAGES } from "../../global/constants/accessConstants";
import { scheduleTokenRefresh } from "../../global/proactiveTokenRefresh";
import { isRedisSessionEnabled } from "../../configuration";

const Login = (props) => {
  let navigate = useNavigate();
  const apiError = useSelector((state) => state.reportReducer.apiError);
const [date, setDate] = useState(new Date());
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState("");
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoginSso, setIsLoginSso] = useState(true);
  const isMFAEnabled = window.REACT_APP_IS_MFA_ENABLED === "true";

  const getCookie = (name) => {
    const cookies = document.cookie.split("; ");
    for (let cookie of cookies) {
      const [key, value] = cookie.split("=");
      if (key === name) {
        return value;
      }
    }
    return null;
  };

  useEffect(() => {
    // Handle refresh-failure redirect (URL param instead of sessionStorage — clears on browser refresh)
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "refresh_failed" || params.get("reason") === "session_expired") {
      setSessionExpiredMsg(LOGIN_ERROR_MESSAGES.SESSION_EXPIRED);
      // Don't auto-login right after a session failure — retrying immediately would loop
      sessionStorage.setItem("_skipAutoLogin", "true");
      // Strip param from URL so browser refresh doesn't re-show it
      window.history.replaceState({}, "", window.location.pathname);
    }

    const sessionErrorMsg = sessionStorage.getItem(STORAGE.SESSION_ERROR_MSG);
    if (sessionErrorMsg) {
      setSessionExpiredMsg(sessionErrorMsg);
      // Do NOT remove key here. On session timeout redirect, this component mounts twice:
      // 1st mount: SPA re-render triggered by resetApp() — reads key, shows message briefly
      // 2nd mount: full page reload from window.location.replace() — must still find the key to show message
      // Cleanup happens in proceedWithoutError() on successful login (covers both MFA and non-MFA paths)
    }

    if (location?.pathname.indexOf("loginsso") > -1) {
      handleBusinessClick();
    } else {
      setIsLoginSso(false);

      const fetchAndRedirectWithAccessToken = async () => {
        // Skip auto-login if session just failed — retrying would loop
        if (sessionStorage.getItem("_skipAutoLogin")) {
          sessionStorage.removeItem("_skipAutoLogin");
          return;
        }

        // Read from activeSession cookie — written by proceedWithoutError on successful login.
        // Present on 2nd+ load (both local and OIDC). NOT present on first OIDC SSO load.
        let parsedSession = null;
        try {
          const cookieValue = getCookie("activeSession");
          parsedSession = cookieValue ? JSON.parse(decodeURIComponent(cookieValue)) : null;
        } catch {
          // invalid or missing cookie, fall through to raw token check
        }
        const accessToken = parsedSession?.sessionToken;
        const refreshToken = parsedSession?.refreshToken;
        const id_token = parsedSession?.idToken;

        if (accessToken && refreshToken) {
          // Legacy mode: tokens already stored in activeSession from a previous login
          setLoading(true);

          let activeSession = {
            sessionToken: accessToken,
            refreshToken: refreshToken,
            idToken: id_token || '',
          };
          dispatch(setUserActiveSession({ activeSession }));

          proceedWithoutError({
            error: false,
            access_token: accessToken,
            refresh_token: refreshToken,
            id_token,
          });
          return;
        }

        // Legacy OIDC first load: sasp-unified sets raw token cookies after SSO redirect
        // (5gVI-mybiz_access-token etc.) but does NOT set activeSession. Read them directly.
        // This was the original pre-Redis behavior, removed by PR #1798 when Redis was introduced.
        if (!isRedisSessionEnabled()) {
          const rawAccessToken = getCookie("5gVI-mybiz_access-token");
          const rawRefreshToken = getCookie("5gVI-mybiz_refresh-token");
          const rawIdToken = getCookie("5gVI-mybiz_id_token");
          if (rawAccessToken && rawRefreshToken) {
            setLoading(true);
            let activeSession = {
              sessionToken: rawAccessToken,
              refreshToken: rawRefreshToken,
              idToken: rawIdToken || '',
            };
            dispatch(setUserActiveSession({ activeSession }));
            proceedWithoutError({
              error: false,
              access_token: rawAccessToken,
              refresh_token: rawRefreshToken,
              id_token: rawIdToken,
            });
            return;
          }
        }

        // Redis session mode: check for companion cookie (readable by JS).
        // vision_session_id is httpOnly (invisible to JS), so detect Redis mode via 5gVI-mybiz-islogin
        // which sasp-unified sets in both Redis AND legacy flows. With isRedisSessionEnabled() guard,
        // this safely detects Redis mode without false-positives on legacy OIDC paths.
        const isLoggedIn = getCookie("5gVI-mybiz-islogin");

        if (isLoggedIn === "true" && isRedisSessionEnabled() && (!parsedSession || parsedSession?.isRedisMode === true)) {
          // Redis mode: sessionId in httpOnly cookie. Send placeholder; middleware reads cookie.
          setLoading(true);
          let activeSession = {
            sessionState: 'redis-session',
            userId: parsedSession?.userId || '',
            accountId: parsedSession?.accountId || '',
            isRedisMode: true,
          };
          dispatch(setUserActiveSession({ activeSession }));

          await proceedWithoutError({
            error: false,
            session_state: 'redis-session',
            isRedisMode: true,
          });
          return;
        }

        // No authentication found
        return;
      };
      fetchAndRedirectWithAccessToken();
    }
  }, []);

  const handleClick = () => {
    navigate("/forgotPassword");
  };

  const onLoginClicked = async () => {
    setLoginAttempted(true);
    setLoading(true);
    const rurl = sessionStorage.getItem(STORAGE.RURL);
    sessionStorage.clear();
    if (rurl && rurl !== "/dashboard" && rurl !== "/login") {
      sessionStorage.setItem(STORAGE.RURL, rurl);
    }
    //Fetch the session/tokens from backend
    const selectedAccount = {
      userName: userName.trim(),
      password: password.trim(),
    };
    try {
      const sessionResponse = await tokenApi(selectedAccount);
      const sessionMetadata = sessionResponse;
      if (!sessionMetadata.error) {
        if (sessionMetadata.access_token) {
          let activeSession = {
            userName: userName,
            sessionToken: sessionMetadata.access_token,
            refreshToken: sessionMetadata.refresh_token,
            idToken: sessionMetadata.id_token || '',
          };
          dispatch(setUserActiveSession({ activeSession }));
        } else {
          let activeSession = {
            userName: userName,
            sessionState: sessionMetadata.session_state || '',
            createdAt: sessionMetadata.createdAt,
            expiresAt: sessionMetadata.expiresAt,
            isRedisMode: true,
          };
          dispatch(setUserActiveSession({ activeSession }));
        }

        await proceedWithoutError(sessionMetadata);
      } else if (sessionMetadata.error) {
        setLoading(false);
        setLoginError(true);
        setErrorText(sessionMetadata.error_description);
      }
    } catch (err) {
      setLoading(false);
      setLoginError(true);
      setErrorText(LOGIN_ERROR_MESSAGES.CONNECTION_FAILED);
    }
  };

  const proceedWithoutError = async (sessionMetadata) => {
    // Clear session expiry message on successful login (prevents stale message on next visit to /login)
    sessionStorage.removeItem(STORAGE.SESSION_ERROR_MSG);
    // Redis ON: sessionMetadata has session_state (no access_token)
    // Redis OFF: sessionMetadata has access_token
    try {
      dispatch(setGetHierarchyLoading(true));

      // For Redis mode, accountDetailsApi will use sessionId from cookies
      // For direct token mode, pass the access_token
      const accountDetails = await accountDetailsApi(
        sessionMetadata?.access_token
      );

      sessionStorage.setItem(STORAGE.ACCOUNT_DETAILS, JSON.stringify(accountDetails));
      sessionStorage.setItem(STORAGE.ACCOUNT_ID, accountDetails.billingaccountid);
      // Signal NewApp to skip accountDetails re-fetch and fire role+license+billing in parallel
      sessionStorage.setItem(STORAGE.FRESH_LOGIN, "true");
      sessionStorage.setItem(STORAGE.UPDATED_DATE, date);

      let widgetConfig = accountDetails?.customdata?.widgetconfig ?? [];
      dispatch(updateWidgetConfig(widgetConfig));

      // Build activeSession with proper fields based on Redis mode
      let activeSession = {
        userId: accountDetails.id,
        userName: userName,
        accountId: accountDetails.billingaccountid,
      };

      if (sessionMetadata.access_token) {
        // Redis OFF: Store tokens directly
        activeSession.sessionToken = sessionMetadata.access_token;
        activeSession.refreshToken = sessionMetadata.refresh_token;
        activeSession.idToken = sessionMetadata.id_token || '';
      } else {
        // Redis ON: Store session state placeholder; middleware reads actual token from cookie
        activeSession.sessionState = sessionMetadata.session_state || 'redis-session';
        activeSession.isRedisMode = true;
        activeSession.createdAt = sessionMetadata.createdAt;
        activeSession.expiresAt = sessionMetadata.expiresAt;
      }

      const expiryMins = sessionMetadata.expires_in
        ? sessionMetadata.expires_in / 60
        : sessionMetadata.expiresAt
          ? Math.max(Math.floor((sessionMetadata.expiresAt - Date.now()) / 60000), 5)
          : 120;
      const cookiePayload = sessionMetadata.access_token
        ? activeSession  // legacy: full session with tokens
        : { accountId: activeSession.accountId, userId: activeSession.userId, sessionState: activeSession.sessionState, isRedisMode: true };
      setCookie("activeSession", JSON.stringify(cookiePayload), expiryMins);
      dispatch(setUserActiveSession({ activeSession }));
      // Legacy mode only: proactively refresh before token expires
      if (sessionMetadata.access_token) {
        scheduleTokenRefresh(sessionMetadata.expires_in);
      }
    } catch (err) {
      setLoading(false);
      setLoginError(true);
      setErrorText(LOGIN_ERROR_MESSAGES.ACCOUNT_DETAILS_FAILED);
    }
  };

  const handleRememberMe = () => {
    setRememberMe((prevState) => !prevState);
  };

  function useDidMount() {
    const [didMount, setDidMount] = useState(false);
    useEffect(() => {
      setDidMount(true);
      dispatch(setDefaultAPIError());
    }, []);

    return didMount;
  }

  const didMount = useDidMount();

  useEffect(() => {
    if (didMount && loginAttempted) {
      if (apiError) {
        setLoading(false);
        setLoginError(true);
        setErrorText(
          LOGIN_ERROR_MESSAGES.ACCOUNT_MISMATCH
        );
      } else {
        setLoading(false);
        setLoginError(false);
        // Use replace:true so the login page doesn't remain in the history stack
        if (location.pathname && location.pathname !== "/login") {
          navigate(location.pathname + location.search, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      }
    }
  }, [apiError]);


  const handleBusinessClick = () => {
    let signInUrl =
      window.REACT_APP_ENV !== "dev"
        ? signInConfig.oidc_provider.authorization_endpoint +
          `?` +
          `client_id=${signInConfig.client_credentials.client_id}&` +
          `redirect_uri=${signInConfig.client_credentials.redirect_uri}&` +
          `scope=${signInConfig.scopes}&` +
          `response_type=code&` +
          `kc_idp_hint=${signInConfig.oidc_provider.kc_idp_hint}`
        : signInConfig.oidc_provider.authorization_endpoint +
          `?` +
          `client_id=${signInConfig.client_credentials.client_id}&` +
          `redirect_uri=${signInConfig.client_credentials.redirect_uri}&` +
          `scope=${signInConfig.scopes}&` +
          `kc_idp_hide=true` +
          `response_type=code`;
    window.location.replace(signInUrl);
  };

  return (
    <>
      {isLoginSso ? (
        <Loader fullscreen={true} active={true} disableFocusLock surface="light" />
      ) : isMFAEnabled ? (
        <>
          <FiveGVideoInsightsPage />
          {sessionExpiredMsg && (
            <div style={{ position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)", backgroundColor: "#fff", border: "1px solid #fe0404", borderRadius: 4, padding: "12px 24px", color: "#fe0404", fontWeight: "bold", zIndex: 9999, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
              {sessionExpiredMsg}
            </div>
          )}
          {loading && (
            <Loader fullscreen={false} active={true} surface="light" />
          )}
        </>
      ) : (
        <div style={{ display: "flex", height: "100%", width: "100%" }}>
          <div
            style={{
              width: "50%",
              backgroundColor: "#000000",
              display: "flex",
            }}
          >
            <div
              style={{
                alignItems: "center",
                display: "flex",
                marginLeft: "30%",
              }}
            >
              <h4
                style={{
                  color: "#FFFFFF",
                  fontWeight: "700px",
                  fontSize: "44.2px",
                  lineHeight: "50px",
                }}
              >
                {""}
                5G Video Insights
              </h4>
            </div>
          </div>
          {/* <div style={{ height: "calc(100vh - 50px)" }}> */}
          <div style={{ alignContent: "center", marginLeft: "15%" }}>
            <br />
            <br />
            <h1
              className="card-title text-center vz-font-h1 font-face-vzb"
              align="center"
              fontWeight="300px"
              fontSize="32px"
            >
              Welcome to 5G Video Insights
            </h1>
            <br />
            <form align="center" className="sasp_login_form">
              {sessionExpiredMsg && (
                <div style={{ fontWeight: "bold", color: "#fe0404" }}>
                  {sessionExpiredMsg}
                </div>
              )}
              {loginError && (
                <div style={{ fontWeight: "bold", color: "#fe0404" }}>
                  {errorText}
                </div>
              )}
              <div style={{ marginLeft: "25px", marginTop: "10px" }}>
                <div>
                  <Input
                    type="text"
                    width="342px"
                    label="User name"
                    placeholder="Enter Username"
                    readOnly={false}
                    required={true}
                    disabled={false}
                    error={false}
                    errorText="Enter a valid name."
                    data-testid="login_usernameInput"
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    color: " #6f7171",
                    marginRight: "80px",
                  }}
                >
                  Your log in name is the email used for this account
                </span>
                <div style={{ marginTop: "15px" }}>
                  <Input
                    width="342px"
                    placeholder="Enter Password"
                    type="password"
                    label="Password"
                    mask="toggle"
                    readOnly={false}
                    required={true}
                    disabled={false}
                    error={false}
                    data-testid="login_enterPasswordInput"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {/* {isDevEnvironment && (
                <div style={{ marginTop: "15px", width: "92%" }}>
                  <DropdownSelect
                    label="Role"
                    errorText="Select a Role"
                    error={false}
                    disabled={false}
                    readOnly={false}
                    value={selectedRole}
                    onChange={handleRoleSelected}
                  >
                    <option value="">Select a Role</option>
                    <option value="Administrator">Administrator</option>
                    <option value="Manager">Manager</option>
                    <option value="Viewer">Viewer</option>
                    <option value="Architect">Architect</option>
                  </DropdownSelect>
                </div>
              )} */}
              </div>
              <br />
              <div className="password_set">
                <div>
                  <input
                    type="checkbox"
                    value={rememberMe}
                    data-testid="login_rememberMeCheckbox"
                    onChange={handleRememberMe}
                  />
                  <p>Remember me</p>
                </div>
                <span
                  data-testid="login_forgotPasswordText"
                  onClick={handleClick}
                  style={{
                    fontSize: "12px",
                    color: "#6F7171",
                    cursor: "pointer",
                  }}
                >
                  Forgot password?
                </span>
              </div>
              <br />
              {!loading && (
                <Button
                  type="primary"
                  className="login-pageButton"
                  align={"center"}
                  data-testid="login_loginButton"
                  onClick={onLoginClicked}
                  disabled={errorText === "Invalid account type"}
                >
                  Login
                </Button>
              )}
              <br />
              <br />
              <div style={{ marginLeft: "50px" }}>
                <TextLinkCaret
                  iconPosition="right"
                  data-testid="login_loginWithVzBusinessTextLinkCaret"
                  onClick={handleBusinessClick}
                >
                  Log in with Verizon My Business
                </TextLinkCaret>
              </div>
              <br />
              <br />
              <p style={{ fontSize: "12px", fontWeight: "400px" }}>
                By continuing, you agree to the{" "}
                <span>
                  <a
                    href="https://www.verizonenterprise.com/terms?adobe_mc=MCMID%3D79489331358806469974203692604025620162%7CMCORGID%3D7ED836675AB3A4860A495CAD%2540AdobeOrg%7CTS%3D1662453824"
                    className="tcFooterLink"
                  >
                    Terms &amp; Conditions
                  </a>
                </span>{" "}
                and Privacy policy.
              </p>
              {loading && (
                <Loader fullscreen={false} active={true} surface="light" />
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
};
export default Login;
